import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangleIcon, DownloadIcon, PrinterIcon, XIcon } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { LeaveSnapshot, Payslip, PayLine } from '../../../types/hr';
import { formatAmount, formatDate, formatPeriod, maskAccount, sumKwacha } from '../../../utils/hrFormat';
import { safeFilename } from '../../../utils/hrExport';
import { Button } from '../../../components/ui/Button';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png';
type PayslipDocumentProps = {
  payslips: Payslip[];
  onClose: () => void;
};

/** A4 payslip preview with Print and PDF download — one page per payslip (bulk supported). */
export function PayslipDocument({
  payslips,
  onClose
}: PayslipDocumentProps) {
  const pageRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState('');
  const bulk = payslips.length > 1;
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && progress === null) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, progress]);
  const handlePrint = () => {
    document.body.classList.add('printing-test-modal');
    window.print();
    window.setTimeout(() => document.body.classList.remove('printing-test-modal'), 500);
  };
  const handleDownload = async () => {
    setError('');
    setProgress(0);
    try {
      await document.fonts?.ready;
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      for (let i = 0; i < payslips.length; i += 1) {
        const node = pageRefs.current[i];
        if (!node) throw new Error('Payslip page not rendered');
        const canvas = await html2canvas(node, {
          scale: 2,
          useCORS: true,
          backgroundColor: '#ffffff',
          logging: false
        });
        if (i > 0) pdf.addPage();
        pdf.addImage(canvas.toDataURL('image/jpeg', 0.92), 'JPEG', 0, 0, 210, 297);
        setProgress(i + 1);
      }
      const first = payslips[0];
      pdf.save(bulk ? `Payslips_${first.period}_${first.runReference}.pdf` : `${first.payslipNumber}_${safeFilename(first.data.name)}.pdf`);
    } catch (err) {
      console.error('Payslip PDF generation failed', err);
      setError('The payslip PDF could not be generated. Please try again.');
    } finally {
      setProgress(null);
    }
  };
  const busy = progress !== null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 print:bg-white print:p-0" role="dialog" aria-modal="true" aria-labelledby="payslip-doc-title" data-id="element-1701" data-mp="3nhd9zw63eh3h">
      <div className="test-modal-print flex max-h-[95vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-slate-100 shadow-2xl print:max-h-none print:max-w-none print:overflow-visible print:rounded-none print:bg-white print:shadow-none" data-id="element-1702" data-mp="17shm2ak59nj">
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-white px-5 py-3 print:hidden" data-id="element-1703" data-mp="1k78is7x1ptwx">
          <div data-id="element-1704" data-mp="1oz0banzri9pp">
            <h2 id="payslip-doc-title" className="text-lg font-bold text-slate-900" data-id="element-1705" data-mp="1oyqgmn3rvedo">
              {bulk ? `${payslips.length} payslips — ${formatPeriod(payslips[0].period)}` : `Payslip ${payslips[0].payslipNumber}`}
            </h2>
            <p className="text-sm text-slate-500" data-id="element-1706" data-mp="1f5sw6bf7zfcf">A4 on BMED Medical Centre letterhead{bulk ? ' · one page per employee' : ''}</p>
          </div>
          <div className="flex items-center gap-2" data-id="element-1707" data-mp="27x2rcdac0wbd">
            <Button variant="outline" size="sm" onClick={handlePrint} disabled={busy} data-id="element-1708" data-mp="27ugogqydz4zh"><PrinterIcon className="mr-1.5 h-4 w-4" aria-hidden="true" data-id="element-1709" data-mp="1twjmff15y6df" /> Print</Button>
            <Button size="sm" onClick={handleDownload} isLoading={busy} disabled={busy} data-id="element-1710" data-mp="1yuthzqqsv8zk">
              {!busy && <DownloadIcon className="mr-1.5 h-4 w-4" aria-hidden="true" data-id="element-1711" data-mp="ih3gmrr2eytm" />}
              {busy ? `Generating ${progress}/${payslips.length}…` : 'Download PDF'}
            </Button>
            <button type="button" onClick={onClose} disabled={busy} className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500" aria-label="Close payslip" data-id="element-1712" data-mp="12ro41gk2zlt9">
              <XIcon className="h-5 w-5" data-id="element-1713" data-mp="t1meslfzrxcr" />
            </button>
          </div>
        </header>
        {error && <p role="alert" className="flex items-center gap-2 border-b border-red-200 bg-red-50 px-5 py-2 text-sm text-red-800 print:hidden" data-id="element-1714" data-mp="p12itl75mzs7">
            <AlertTriangleIcon className="h-4 w-4" aria-hidden="true" data-id="element-1715" data-mp="1gqnglpubka92" /> {error}
          </p>}
        <div className="flex-1 overflow-auto p-6 print:overflow-visible print:p-0" data-id="element-1716" data-mp="1cok3kws04xlo">
          <div className="mx-auto flex w-[210mm] flex-col gap-6 print:w-auto print:gap-0" data-id="element-1717" data-mp="17ppn41hibl4q">
            {payslips.map((slip, index) => <div key={slip.id} ref={node => {
            pageRefs.current[index] = node;
          }} className="relative flex h-[297mm] w-[210mm] flex-col overflow-hidden bg-white px-[14mm] py-[12mm] text-[12px] leading-snug text-slate-900 shadow-md print:h-auto print:min-h-0 print:w-auto print:break-after-page print:p-0 print:shadow-none print:last:break-after-auto" data-id="element-1718" data-mp="1aon9kpd2isv" data-mpi={index}>
                <PayslipPage slip={slip} data-id="element-1719" data-mp="2cumjxq4hqrt6" />
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}
const EARNING_ROWS: {
  code: string;
  label: string;
}[] = [{
  code: 'basic',
  label: 'Basic Salary'
}, {
  code: 'housing',
  label: 'Housing Allowance'
}, {
  code: 'transport',
  label: 'Transport Allowance'
}, {
  code: 'night-duty',
  label: 'Night Duty Allowance'
}, {
  code: 'overtime',
  label: 'Overtime'
}];
const DEDUCTION_ROWS: {
  code: string;
  label: string;
}[] = [{
  code: 'paye',
  label: 'PAYE'
}, {
  code: 'napsa',
  label: 'NAPSA'
}, {
  code: 'nhima',
  label: 'NHIMA'
}, {
  code: 'salary-advance',
  label: 'Salary Advance'
}, {
  code: 'loan',
  label: 'Loan Repayment'
}];
function arrange(lines: PayLine[], fixed: {
  code: string;
  label: string;
}[]) {
  const rows = fixed.map(row => ({
    label: row.label,
    amount: sumKwacha(lines.filter(l => l.code === row.code).map(l => l.amount))
  }));
  const known = new Set(fixed.map(row => row.code));
  const others = lines.filter(l => !known.has(l.code) && l.amount !== 0).map(l => ({
    label: l.label,
    amount: l.amount
  }));
  return [...rows, ...others];
}
const days = (value: number) => Number.isInteger(value) ? String(value) : value.toFixed(1);
function LeaveSummary({
  leave
}: {
  leave: LeaveSnapshot;
}) {
  const rows: [string, string][] = [['Opening Leave Balance', days(leave.openingBalance)], ['Leave Accrued This Month', days(leave.accrued)], ['Total Leave Taken This Month', days(leave.taken)], ...(leave.adjusted !== 0 ? [['Approved Adjustments', `${leave.adjusted > 0 ? '+' : ''}${days(leave.adjusted)}`]] as [string, string][] : []), ...(leave.forfeited > 0 ? [['Forfeited at Year End (carry-forward limit)', `−${days(leave.forfeited)}`]] as [string, string][] : [])];
  return <section className="mt-4" aria-label="Employee leave summary" data-id="element-1720" data-mp="3txdbnw3ng2hv">
      <table className="w-full border-collapse text-[11px]" data-id="element-1721" data-mp="3txwxhnwf7b16">
        <thead data-id="element-1722" data-mp="2prd2hvseq7v">
          <tr className="bg-slate-100 text-left uppercase tracking-wide text-slate-700" data-id="element-1723" data-mp="2qakh4nt9w7n">
            <th className="border border-slate-300 px-2 py-1.5" data-id="element-1724" data-mp="3kvnkmrdoj5km">Employee Leave Summary — {leave.leaveTypeName}</th>
            <th className="w-32 border border-slate-300 px-2 py-1.5 text-right" data-id="element-1725" data-mp="3owfvfp279ij0">Days</th>
          </tr>
        </thead>
        <tbody data-id="element-1726" data-mp="3b4j77w3njsrw">
          {rows.map(([label, value]) => <tr key={label} data-id="element-1727" data-mp="3fydwdf210zyd">
              <td className="border border-slate-300 px-2 py-1" data-id="element-1728" data-mp="3fyni2qg1gky9">{label}</td>
              <td className="border border-slate-300 px-2 py-1 text-right tabular-nums" data-id="element-1729" data-mp="319r6wkioqrtf">{value}</td>
            </tr>)}
          <tr className="bg-slate-50 text-[12px] font-bold" data-id="element-1730" data-mp="365gdh7t4h8z8">
            <td className="border border-slate-300 px-2 py-1.5" data-id="element-1731" data-mp="2s8t60b3jn0f2">Accumulated Available Leave Balance</td>
            <td className="border border-slate-300 px-2 py-1.5 text-right tabular-nums" data-id="element-1732" data-mp="2x5jiypk1xfw9">{days(leave.closingBalance)}</td>
          </tr>
        </tbody>
      </table>
      <p className="mt-1 text-[10px] text-slate-500" data-id="element-1733" data-mp="37q16cu7j3oo2">
        {leave.eligible ? `Accrues ${days(leave.daysPerMonth)} days per completed month of service · commenced ${formatDate(leave.commencementDate)} · ${leave.completedMonths} completed month${leave.completedMonths === 1 ? '' : 's'} · ${days(leave.totalAccrued)} days accrued to date.` : 'This employment type does not accrue monthly leave under the hospital leave policy.'}
      </p>
    </section>;
}
function PayslipPage({
  slip
}: {
  slip: Payslip;
}) {
  const d = slip.data;
  const earnings = arrange(d.earnings, EARNING_ROWS);
  const deductions = arrange(d.deductions, DEDUCTION_ROWS);
  const table = (title: string, rows: {
    label: string;
    amount: number;
  }[], totalLabel: string, total: number) => <table className="w-full border-collapse" data-id="element-1734" data-mp="2b1bpn77n123o">
      <thead data-id="element-1735" data-mp="261gwsy4ay2fz">
        <tr className="bg-slate-100 text-left text-[11px] uppercase tracking-wide text-slate-700" data-id="element-1736" data-mp="260xq6dk3r0h9">
          <th className="border border-slate-300 px-2 py-1.5" data-id="element-1737" data-mp="lhv8b19pqwl2">{title}</th>
          <th className="w-32 border border-slate-300 px-2 py-1.5 text-right" data-id="element-1738" data-mp="qgom6uswwplr">Amount (ZMW)</th>
        </tr>
      </thead>
      <tbody data-id="element-1739" data-mp="ujl0i9bf5zjw">
        {rows.map((row, i) => <tr key={`${row.label}-${i}`} data-id="element-1740" data-mp="zhwack1psa24" data-mpi={i}>
            <td className="border border-slate-300 px-2 py-1" data-id="element-1741" data-mp="zguzogom1pkx">{row.label}</td>
            <td className="border border-slate-300 px-2 py-1 text-right tabular-nums" data-id="element-1742" data-mp="2soo19t9twx5">{formatAmount(row.amount)}</td>
          </tr>)}
        <tr className="bg-slate-50 font-bold" data-id="element-1743" data-mp="6tqn9qn6u8z4">
          <td className="border border-slate-300 px-2 py-1.5" data-id="element-1744" data-mp="bs1zu94vaxl1">{totalLabel}</td>
          <td className="border border-slate-300 px-2 py-1.5 text-right tabular-nums" data-id="element-1745" data-mp="gja7azmn08vk">{formatAmount(total)}</td>
        </tr>
      </tbody>
    </table>;
  return <>
      {slip.status === 'Void' && <p className="pointer-events-none absolute inset-0 flex items-center justify-center text-[96px] font-black uppercase tracking-widest text-red-600/15 [transform:rotate(-30deg)]" aria-hidden="true" data-id="element-1746" data-mp="3k83uskayzmfe">Void</p>}
      <header className="flex items-start justify-between gap-6 border-b-2 border-slate-900 pb-4" data-id="element-1747" data-mp="v453qwxfsgo8">
        <div className="flex items-center gap-4" data-id="element-1748" data-mp="103q5tg7wl556">
          <img src={LOGO_URL} alt="BMED Medical Centre" crossOrigin="anonymous" className="h-20 w-20 shrink-0 object-contain" data-id="element-1749" data-mp="101wu3mifnuqb" />
          <div data-id="element-1750" data-mp="7flxso1v0fzb">
            <p className="text-2xl font-bold tracking-wide text-pink-600" data-id="element-1751" data-mp="7g3rbcx50g3j">BMED MEDICAL CENTRE</p>
            <p className="italic text-slate-600" data-id="element-1752" data-mp="cdng36rgob9q">Caring Healing Leading</p>
          </div>
        </div>
        <address className="text-right text-[11px] not-italic leading-relaxed text-slate-700" data-id="element-1753" data-mp="hd8cb9f8y5ir">
          <p data-id="element-1754" data-mp="m4i1ya0sp8un">Plot No. 1884/10, Along Avic Road, Lilayi Estate</p>
          <p data-id="element-1755" data-mp="m61gnw6szbub">Kafue, Lusaka - Zambia</p>
          <p data-id="element-1756" data-mp="3kv09r3c5jhoc">Contact: +260975202011</p>
          <p data-id="element-1757" data-mp="3ktyroewpxux4">Email: bmedhospital@gmail.com</p>
          <p data-id="element-1758" data-mp="3pss1nxcsr3sj">TPIN: 2000869447</p>
        </address>
      </header>

      <div className="mt-4 flex items-end justify-between" data-id="element-1759" data-mp="3pqyumgy9uhk0">
        <h3 className="text-xl font-bold uppercase tracking-wider" data-id="element-1760" data-mp="3i8hjy7enswpg">Payslip</h3>
        <div className="text-right" data-id="element-1761" data-mp="3dag1nop251f1">
          <p className="text-[11px] uppercase tracking-wide text-slate-500" data-id="element-1762" data-mp="3dbharnmijdu4">Payslip No.</p>
          <p className="font-mono text-base font-bold" data-id="element-1763" data-mp="38a1r9zzzhfdu">{slip.payslipNumber}</p>
        </div>
      </div>

      <dl className="mt-3 grid grid-cols-3 gap-x-6 gap-y-2 rounded border border-slate-300 p-3" data-id="element-1764" data-mp="33c9zd5zc9t9n">
        {[['Employee name', d.name], ['Employee ID', d.employeeNumber], ['Department', d.departmentName], ['Position', d.positionTitle], ['Payroll month', formatPeriod(slip.period)], ['Payment date', formatDate(slip.paymentDate)], ['NAPSA no.', d.napsaNumber || '—'], ['NHIMA no.', d.nhimaNumber || '—'], ['TPIN', d.tpin || '—']].map(([label, value]) => <div key={label} data-id="element-1765" data-mp="2bku64t3a18ag">
            <dt className="text-[10px] uppercase tracking-wide text-slate-500" data-id="element-1766" data-mp="2bmneqdxga2l3">{label}</dt>
            <dd className="font-semibold" data-id="element-1767" data-mp="20zgyl3fdfsff">{value}</dd>
          </div>)}
      </dl>

      <div className="mt-4 grid grid-cols-2 gap-4" data-id="element-1768" data-mp="260eetagtmcea">
        {table('Earnings', earnings, 'GROSS PAY', d.gross)}
        {table('Deductions', deductions, 'TOTAL DEDUCTIONS', d.totalDeductions)}
      </div>

      <section className="mt-5 grid grid-cols-[1fr_auto] items-end gap-6 rounded border-2 border-slate-900 p-4" data-id="element-1769" data-mp="1wzopmpm2fk9j">
        <dl className="space-y-1 text-[13px]" data-id="element-1770" data-mp="2korf2jmxyhel">
          <div className="flex gap-3" data-id="element-1771" data-mp="2opl2lbwh1u6y"><dt className="w-40 font-semibold" data-id="element-1772" data-mp="2oorr5xmkigih">GROSS SALARY:</dt><dd className="tabular-nums" data-id="element-1773" data-mp="2ornqnqe2dd91">K {formatAmount(d.gross)}</dd></div>
          <div className="flex gap-3" data-id="element-1774" data-mp="2axwo51yr2tmt"><dt className="w-40 font-semibold" data-id="element-1775" data-mp="2awlkr397vnw8">TOTAL DEDUCTIONS:</dt><dd className="tabular-nums" data-id="element-1776" data-mp="2fw7z5g0s7a6m">K {formatAmount(d.totalDeductions)}</dd></div>
        </dl>
        <div className="text-right" data-id="element-1777" data-mp="33mbs8tgcfrjd">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-600" data-id="element-1778" data-mp="33l91clj0tp3g">Net Salary</p>
          <p className="text-3xl font-extrabold tabular-nums" data-id="element-1779" data-mp="38fn45900iyi5">K {formatAmount(d.net)}</p>
        </div>
      </section>

      <div className="mt-4 grid grid-cols-2 gap-4 text-[11px]" data-id="element-1780" data-mp="3dra90wrfkb1f">
        <div className="rounded border border-slate-300 p-3" data-id="element-1781" data-mp="3doxwx08csuwa">
          <p className="mb-1 font-semibold uppercase tracking-wide text-slate-600" data-id="element-1782" data-mp="3rhds5ev0jomn">Payment details</p>
          <p data-id="element-1783" data-mp="3mkdofalmyxyp">Bank: {d.bankName || '—'}{d.bankBranch ? `, ${d.bankBranch}` : ''}</p>
          <p data-id="element-1784" data-mp="2ysztcww3ooa1">Account name: {d.bankAccountName || '—'}</p>
          <p data-id="element-1785" data-mp="2yvkxxtns9ibh">Account no.: {maskAccount(d.bankAccountNumber)}</p>
        </div>
        <div className="rounded border border-slate-300 p-3" data-id="element-1786" data-mp="2utr2q0wcx6ip">
          <p className="mb-1 font-semibold uppercase tracking-wide text-slate-600" data-id="element-1787" data-mp="38s7gucwjtzav">Employer contributions (not deducted from pay)</p>
          {d.employer.map(line => <p key={line.code} className="flex justify-between" data-id="element-1788" data-mp="o1q5mmufcbqu"><span data-id="element-1789" data-mp="o0f6x1fu5u6l">{line.label}</span><span className="tabular-nums" data-id="element-1790" data-mp="o3t5dyldpj8r">{formatAmount(line.amount)}</span></p>)}
        </div>
      </div>

      {d.leave && <LeaveSummary leave={d.leave} data-id="element-1791" data-mp="2or5v43vxwj8i" />}

      <footer className="mt-auto border-t border-slate-300 pt-3 text-[10px] text-slate-500" data-id="element-1792" data-mp="2os7fx02rck3f">
        <p data-id="element-1793" data-mp="2tqs7kb3y1vg9">Payroll reference {slip.runReference} · generated {formatDate(slip.generatedAt)}. This is a computer-generated payslip and is confidential to the named employee.</p>
      </footer>
    </>;
}